import { faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Divider,
  Group,
  PasswordInput,
  TextInput,
  UnstyledButton
} from '@mantine/core';

import { useForm } from '@mantine/form';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { createB2BAPI } from '../../api/Interceptor';
import notify from '../../utils/Notification';

import B2BButton from '../common/B2BButton';

import './Login.css';

export function Login({ reloadHeader }) {

  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [lockoutTimeLeft, setLockoutTimeLeft] = useState(0);

  const [loginMethod, setLoginMethod] = useState('email');

  const [otpSent, setOtpSent] = useState(false);

  const [otpLoading, setOtpLoading] = useState(false);

  const [timer, setTimer] = useState(0);

  const B2B_API = useMemo(() => createB2BAPI(navigate), [navigate]);

  const queryParams = new URLSearchParams(location.search);

  const role = queryParams.get('role') || 'customer';

  const from = queryParams.get('from');

  const headingText = role === 'vendor' ? 'Seller' : 'Buyer';

  const SignUp = role === 'vendor' ? '/SignUpVendor' : '/signup';

  const loginRole =
    role === 'vendor' ? 'vendor/login' : 'customer/login';

  const homePage =
    role === 'vendor' ? '/VendorHome' : '/dashboard';

  const { email: prefillEmail, password: prefillPassword } =
    location.state || {};

  useEffect(() => {
    let interval;

    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }

    return () => clearInterval(interval);

  }, [timer]);

  useEffect(() => {
    const lockoutUntil = localStorage.getItem('login_lockout_until');
    if (lockoutUntil) {
      const parsedUntil = parseInt(lockoutUntil, 10);
      const calculateTimeLeft = () => {
        const remaining = Math.ceil((parsedUntil - Date.now()) / 1000);
        return remaining > 0 ? remaining : 0;
      };

      const initialTime = calculateTimeLeft();
      if (initialTime > 0) {
        setLockoutTimeLeft(initialTime);
        const timerInterval = setInterval(() => {
          const remaining = calculateTimeLeft();
          if (remaining <= 0) {
            setLockoutTimeLeft(0);
            localStorage.removeItem('login_lockout_until');
            clearInterval(timerInterval);
          } else {
            setLockoutTimeLeft(remaining);
          }
        }, 1000);
        return () => clearInterval(timerInterval);
      } else {
        localStorage.removeItem('login_lockout_until');
      }
    }
  }, []);

  const form = useForm({
    initialValues: {
      email: prefillEmail || '',
      password: prefillPassword || '',
      mobile: '',
      otp: '',
    },

    validate: {
      email: (val) =>
        loginMethod === 'email'
          ? /^\S+@\S+$/.test(val)
            ? null
            : 'Invalid email'
          : null,

      mobile: (val) =>
        loginMethod === 'otp'
          ? val.length < 10
            ? 'Invalid mobile number'
            : null
          : null,
    },
  });

  const saveUserAndNavigate = (response) => {

    const { token } = response?.response;

    localStorage.setItem('token', token);

    localStorage.setItem(
      'user',
      JSON.stringify(response?.response)
    );

    setLockoutTimeLeft(0);
    localStorage.removeItem('login_lockout_until');

    reloadHeader();

    if (from) {
      navigate(from, { state: { page: 0 } });
    } else {
      navigate(homePage, { state: { page: 0 } });
    }
  };

  const processLoginError = async (error, notifyId) => {
    let message = error?.message;
    if (error?.response) {
      try {
        const errorData = await error.response.clone().json();
        message = errorData.message || message;
      } catch (_) { }
    }

    if (message && message.includes("Too many failed attempts")) {
      const regex = /Too many failed attempts\. Please try again after (?:(\d+)\s+minutes?)?(?:\s*and\s*)?(?:(\d+)\s+seconds?)?\./;
      const match = message.match(regex);
      if (match) {
        const minutes = parseInt(match[1] || 0, 10);
        const seconds = parseInt(match[2] || 0, 10);
        const totalSeconds = minutes * 60 + seconds;

        if (totalSeconds > 0) {
          const lockoutUntil = Date.now() + totalSeconds * 1000;
          localStorage.setItem('login_lockout_until', lockoutUntil);
          setLockoutTimeLeft(totalSeconds);

          const timerInterval = setInterval(() => {
            const remaining = Math.ceil((lockoutUntil - Date.now()) / 1000);
            if (remaining <= 0) {
              setLockoutTimeLeft(0);
              localStorage.removeItem('login_lockout_until');
              clearInterval(timerInterval);
            } else {
              setLockoutTimeLeft(remaining);
            }
          }, 1000);
        }
      }
    }

    notify({
      id: notifyId,
      success: false,
      error: true,
      color: 'red',
      title: 'Login Failed',
      message: message || 'Something went wrong',
    });
  };

  const handleLogin = async () => {

    if (loginMethod === 'otp') {
      handleOtpLogin();
      return;
    }

    setLoading(true);

    try {

      const response = await B2B_API.post(loginRole, {
        json: {
          emailId: form.values.email,
          password: form.values.password,
        },
      }).json();

      saveUserAndNavigate(response);

    } catch (error) {
      await processLoginError(error, 'login-failure');
    } finally {
      setLoading(false);
    }
  };
  const handleGetOtp = async () => {

    const { mobile } = form.values;

    const isMobile = /^[6-9]\d{9}$/.test(mobile);

    const isEmail =
      /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(mobile);

    setOtpLoading(true);

    try {

      await B2B_API.post('otp', {
        json: {
          mobile: isMobile ? mobile : null,
          email: isEmail ? mobile : null,
        },
      }).json();

      setOtpSent(true);

      setTimer(60);

      notify({
        id: 'otp-success',
        success: true,
        color: 'green',
        title: 'OTP Sent',
        message: 'OTP sent successfully',
      });

    } catch (error) {

      notify({
        id: 'otp-failure',
        success: false,
        error: true,
        color: 'red',
        title: 'Failed',
        message: error?.message || 'Failed to send OTP',
      });

    } finally {

      setOtpLoading(false);
    }
  };
  const handleOtpLogin = async () => {

    setLoading(true);

    try {

      const otpLoginRole =
        role === 'vendor'
          ? 'vendor/loginWithOtp'
          : 'customer/loginWithOtp';

      const response = await B2B_API.post(
        otpLoginRole,
        {
          json: {
            mobile: form.values.mobile,
            otp: form.values.otp,
          },
        }
      ).json();
      saveUserAndNavigate(response);

    } catch (error) {
      await processLoginError(error, 'otp-login-failure');
    } finally {
      setLoading(false);
    }
  };

  return (

    <div
      id="login-monday-container"
      className="login-monday-container"
    >

      <div className="login-monday-content-component">

        <div className="router-wrapper">

          <div className="email-first-component">

            <h1 className="login-header">
              {headingText}
              <span style={{ color: 'white' }}>
                {' '}Sign in
              </span>
            </h1>

            {lockoutTimeLeft > 0 && (
              <div className="lockout-banner">
                <FontAwesomeIcon icon={faLock} color="#ef4444" style={{ fontSize: '16px', flexShrink: 0 }} />
                <div className="lockout-info">
                  <h3 className="lockout-title">Login limit exceeded</h3>
                  <p className="lockout-text">
                    Too many failed login attempts. Please wait <span className="lockout-countdown-inline">{lockoutTimeLeft >= 60 ? `${Math.floor(lockoutTimeLeft / 60)}m:${(lockoutTimeLeft % 60).toString().padStart(2, '0')}s` : `${lockoutTimeLeft}s`}</span> before retrying.
                  </p>
                </div>
              </div>
            )}

            <form
              className="login-form"
              onSubmit={form.onSubmit(() => handleLogin())}
            >

              <div className="email-password-field">

                {loginMethod === 'email' ? (

                  <>
                    <TextInput
                      className="login-email"
                      required
                      size="md"
                      radius="sm"
                      placeholder="ex:support@fabinsta.com"
                      value={form.values.email}
                      onChange={(e) =>
                        form.setFieldValue(
                          'email',
                          e.currentTarget.value
                        )
                      }
                      error={form.errors.email}
                    />

                    <Group justify="flex-end" mb={5}>
                      <UnstyledButton
                        onClick={() =>
                          navigate(
                            '/forgot-password?role=' + role
                          )
                        }
                        size="xs"
                        style={{
                          color: '#ffffff',
                          fontSize: '0.75rem',
                        }}
                      >
                        Forgot your password?
                      </UnstyledButton>
                    </Group>

                    <PasswordInput
                      className="login-password"
                      required
                      size="md"
                      radius="sm"
                      placeholder="Your password"
                      value={form.values.password}
                      onChange={(e) =>
                        form.setFieldValue(
                          'password',
                          e.currentTarget.value
                        )
                      }
                    />
                  </>

                ) : (

                  <>
                    <TextInput
                      className="login-email"
                      required
                      size="md"
                      radius="sm"
                      placeholder="Enter Mobile Number Or Email"
                      value={form.values.mobile}
                      onChange={(e) =>
                        form.setFieldValue(
                          'mobile',
                          e.currentTarget.value
                        )
                      }
                      error={form.errors.mobile}
                      rightSection={
                        <UnstyledButton
                          onClick={handleGetOtp}
                          disabled={otpLoading || timer > 0 || lockoutTimeLeft > 0}
                          style={{
                            fontSize: '12px',
                            color:
                              timer > 0
                                ? 'gray'
                                : 'var(--primary)',
                            fontWeight: 600,
                          }}
                        >
                          {
                            otpLoading
                              ? '...'
                              : timer > 0
                                ? `Resend ${timer}s`
                                : otpSent
                                  ? 'Resend'
                                  : 'Get OTP'
                          }
                        </UnstyledButton>
                      }
                      rightSectionWidth={90}
                    />

                    {otpSent && (
                      <TextInput
                        className="login-password"
                        required
                        size="md"
                        radius="sm"
                        placeholder="Enter OTP"
                        value={form.values.otp}
                        onChange={(e) =>
                          form.setFieldValue(
                            'otp',
                            e.currentTarget.value
                          )
                        }
                      />
                    )}

                  </>
                )}

              </div>

              <div className="button-container">

                <B2BButton
                  type="submit"
                  loading={loading}
                  disabled={lockoutTimeLeft > 0}
                  name="Sign in"
                  variant="contained"
                  radius="5"
                  style={{
                    background: 'var(--primary)',
                  }}
                />

              </div>

            </form>

            <div
              style={{
                marginTop: '10px',
                textAlign: 'center',
              }}
            >

              <UnstyledButton
                onClick={() => {

                  setLoginMethod(
                    loginMethod === 'email'
                      ? 'otp'
                      : 'email'
                  );

                  setOtpSent(false);

                }}
                style={{
                  color: '#ffffff',
                  fontSize: '0.9rem',
                  fontWeight: 500,
                }}
              >
                {
                  loginMethod === 'email'
                    ? 'Login with OTP instead'
                    : 'Back to Email Login'
                }
              </UnstyledButton>

            </div>

            <div className="suggest-signup-wrapper">

              <div className="suggest-signup-component">

                <Divider
                  size={'sm'}
                  label={
                    <>
                      <span style={{ color: '#ffffff' }}>
                        Don't have an account yet?&nbsp;
                      </span>

                      <UnstyledButton
                        onClick={() => navigate(SignUp)}
                        size="xs"
                        style={{
                          color: '#8BC53F',
                          fontSize: '1rem',
                          fontWeight: 600,
                        }}
                      >
                        Sign Up
                      </UnstyledButton>
                    </>
                  }
                />

              </div>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
}